import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", form);
      setUser(data);
      const dest = location.state?.from || (data.role === "admin" || data.role === "counsellor" ? "/admin" : "/portal");
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthShell title="Welcome Back" subtitle="Sign in to your XTM Student Portal">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        {error && <div className="bg-red-500/10 border border-red-500/40 text-red-300 text-sm rounded-xl px-4 py-3" data-testid="login-error">{error}</div>}
        <div>
          <Label htmlFor="lg-email">Email</Label>
          <Input id="lg-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="bg-navy-base border-white/15 text-white mt-1" data-testid="login-email-input" />
        </div>
        <div>
          <div className="flex justify-between items-center">
            <Label htmlFor="lg-password">Password</Label>
            <Link to="/forgot-password" className="text-xs text-orange-400 hover:text-orange-300 transition-colors" data-testid="forgot-password-link">Forgot password?</Link>
          </div>
          <Input id="lg-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="bg-navy-base border-white/15 text-white mt-1" data-testid="login-password-input" />
        </div>
        <Button type="submit" disabled={submitting} className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold transition-colors" data-testid="login-submit-btn">
          {submitting ? "Signing in…" : "Sign In"}
        </Button>
      </form>
      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-white/10" /><span className="text-xs text-slate-500">or</span><div className="flex-1 h-px bg-white/10" />
      </div>
      <Button onClick={googleLogin} variant="outline" className="w-full border-white/20 text-white hover:bg-white/5 transition-colors" data-testid="google-login-btn">
        Continue with Google
      </Button>
      <p className="text-sm text-slate-400 mt-6 text-center">
        New here? <Link to="/register" className="text-orange-400 hover:text-orange-300 font-medium transition-colors" data-testid="register-link">Create an account</Link>
      </p>
    </AuthShell>
  );
}

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-navy-base relative flex items-center justify-center px-4 py-12">
      <div className="absolute inset-0 cyber-grid opacity-40" />
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center gap-2.5 justify-center mb-8" data-testid="auth-logo">
          <div className="w-10 h-10 rounded-xl bg-orange-600 flex items-center justify-center">
            <GraduationCap className="w-5 h-5 text-white" />
          </div>
          <div className="leading-tight text-left">
            <div className="font-display font-extrabold text-white">XTM TECHNOLOGIES</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-orange-400 font-semibold">FutureSkills Academy</div>
          </div>
        </Link>
        <div className="bg-card border border-white/10 rounded-3xl p-8">
          <h1 className="font-display text-2xl font-bold text-white mb-1" data-testid="auth-title">{title}</h1>
          <p className="text-sm text-slate-400 mb-6">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}
